/**
 * WallProduct — shared config + display pricing.
 * The REAL charge always comes from the app's checkout (getImagesDB ->
 * AmountCharged); these constants are display-only.
 */
export const BUNDLES = [4, 6, 9, 10, 12, 14, 16, 20, 24, 30];

const FRAME_PRICE = 49;
const HANDLING_FEE_PER_IMAGE = 0.36;
const FREE_SHIPPING_THRESHOLD = 5;
const SHIPPING_PRICE = 35;

const TIERS = [
  { imageCount: 6, discount: 5, enableDefault: true },
  { imageCount: 8, discount: 10, enableDefault: true },
  { imageCount: 9, discount: 20, enableDefault: false, fallbackToLower: true },
  { imageCount: 10, discount: 19.2, enableDefault: true },
  { imageCount: 12, discount: 25, enableDefault: true },
  { imageCount: 16, discount: 27, enableDefault: true },
  { imageCount: 24, discount: 30, enableDefault: true },
];

function resolveTierDiscount(count) {
  const sorted = [...TIERS].sort((a, b) => a.imageCount - b.imageCount);
  let maxTier = null;
  for (const t of sorted) if (t.imageCount <= count) maxTier = t;
  if (!maxTier) return 0;
  if (maxTier.enableDefault) return maxTier.discount || 0;
  if (maxTier.fallbackToLower) {
    let fb = null;
    for (const t of sorted) if (t.imageCount < maxTier.imageCount && t.enableDefault) fb = t;
    if (fb) return fb.discount || 0;
  }
  return 0;
}

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export const money = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

export function priceBundle(count) {
  const net = count * FRAME_PRICE;
  const discountPct = resolveTierDiscount(count);
  const discountAmount = net * (discountPct / 100);
  const handling = count * HANDLING_FEE_PER_IMAGE;
  const shipping = count >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_PRICE;
  const regular = round2(net + handling + shipping);
  const final = Math.max(round2(net - discountAmount + handling + shipping), 2);
  return { count, net: round2(net), discountPct, handling: round2(handling), shipping, freeShip: shipping === 0, regular, final, savings: round2(regular - final) };
}

const COLUMNS = { 4: 2, 6: 3, 9: 3, 10: 5, 12: 4, 14: 5, 16: 4, 20: 5, 24: 6, 30: 6 };
export function wallLayout(count) {
  const cols = COLUMNS[count] || Math.ceil(Math.sqrt(count));
  const gap = 8;
  const gridWidthPct = Math.min(88, 30 + cols * 11); // legacy fallback; superseded by wallGridWidthPct
  return { cols, gap, gridWidthPct };
}

// --- Real-world scene proportioning (see WALL_SCENE_PROPORTIONS.md) ---
// A framed 8×8" print reads ~24 cm outer; hung with ~4 cm gaps. wallGridWidthCm = the
// real width (cm) the placed grid occupies on the wall.
export const FRAME_OUTER_CM = 24;
export const FRAME_GAP_CM = 4;
export const wallGridWidthCm = (cols) => cols * FRAME_OUTER_CM + (cols - 1) * FRAME_GAP_CM;

// How many cm the FULL scene image width spans — from a reference object (3-seat sofa
// ~215 cm at its measured image fraction), +~5% for wall depth. Per scene; default is a
// generic living room. sceneCx nudges the grid horizontally (% of wall) so it sits over
// the empty wall / dodges decor. Measure new scenes per WALL_SCENE_PROPORTIONS.md.
export const DEFAULT_SCENE_SCALE_CM = 320;
// scaleCm + cx live in the SCENES table (single source, below). These accept a scene id
// ('stone'), an env basename ('env-il-stone-living'), or the .webp filename.
export const sceneScaleCm = (env) => sceneByRef(env).scaleCm || DEFAULT_SCENE_SCALE_CM;
// Grid nudges are per-breakpoint: mobile cover-crops the wide scene to its middle, which
// remaps horizontal position, so the same feature (bed/sofa) needs a different offset than
// desktop. cx/cy = desktop (also the ~4:3 catalog thumb); cxM/cyM = mobile product page.
// + cx = physical right; - cy = up. Fall back to the desktop value / 0 when unset.
export const sceneCx = (env) => (sceneByRef(env).grid && sceneByRef(env).grid.cx) || 0;
export const sceneCxM = (env) => { const g = sceneByRef(env).grid; return g && g.cxM != null ? g.cxM : (g && g.cx) || 0; };
export const sceneCy = (env) => (sceneByRef(env).grid && sceneByRef(env).grid.cy) || 0;
export const sceneCyM = (env) => (sceneByRef(env).grid && sceneByRef(env).grid.cyM) || 0;
// Mobile centre-crop: object-position X% of the wall photo (default 50 = centre). Mobile
// cover-crops the wide 3:2 photo to a narrow centre band, so a room whose anchor sits off
// to one side (calm's bed is ~1/3 from the left) gets cropped out of view. Lower posM
// reveals more of the LEFT of the room so the anchor sits in frame; the grid's cxM then
// nudges over it. Desktop shows ~the full photo, so it always uses 50.
export const scenePosM = (env) => { const g = sceneByRef(env).grid; return g && g.posM != null ? g.posM : 50; };
// Mobile-only vertical scene offset: translate the WHOLE scene (room photo + frame grid,
// as one unit) down by this % of the wall height, so the composition sits lower on the
// page without moving the frames relative to the wall. Kept within the mobile zoom's
// overflow (~10% at zoom 1.2) so no background shows. Default 0 (desktop always 0).
export const sceneOffYM = (env) => { const g = sceneByRef(env).grid; return g && g.offYM != null ? g.offYM : 0; };
// Mobile grid-width clamp (% of the wall box). A wide grid's TRUE size can exceed the
// phone width (the portrait crop magnifies the room), so it gets clamped smaller than it
// should be — reads too small vs desktop. Raise maxGwM per wide scene to restore a faithful
// size. Default 72; desktop is a fixed 84 (wide grids don't over-run the larger pane).
export const sceneMaxGwM = (env) => { const g = sceneByRef(env).grid; return (g && g.maxGwM) || 72; };
// Mobile scene-height factor (0-1 of the default full-height box). A SHORTER box is less
// portrait, so the cover-crop magnifies the room LESS — the room reads smaller next to a
// clamped (zoomed-out) gallery. Default 1 (full height). Trade-off: the shorter the scene,
// the more empty space opens below it before the toolbar.
export const sceneHfM = (env) => { const g = sceneByRef(env).grid; return (g && g.hfM) || 1; };
// A scene may ship a gen-fill-expanded TALLER photo for mobile (env-il-<id>-m.webp); mAr is
// its W/H aspect (e.g. 0.8 = 4:5). null -> use the 3:2 original on mobile too.
export const sceneMAr = (env) => sceneByRef(env).mAr || null;

// Non-rectangular grids: `rows` = per-row frame counts ([1,2,3,4,3,2,1] = diamond).
// Rectangular products leave it null and use `cols`; the widest row drives the real width.
export const rowsMaxCols = (rows) => (rows && rows.length ? Math.max(...rows) : 0);

// The env scene photos are 3:2 (1536×1024).
export const SCENE_IMG_ASPECT = 1.5;

// Grid width as a % of the CONTAINER, corrected for object-fit:cover. The frame grid
// must be a fixed fraction of the RENDERED image (so it matches the sofa), but the image
// is cover-cropped by its box — magnified when the box is more portrait than the photo
// (mobile!). containerAspect = width/height of the wall/thumb box. Desktop wall ≈ photo
// aspect -> ~no correction; a tall mobile wall magnifies a lot. See
// WALL_SCENE_PROPORTIONS.md.
// imgAspect = the actual photo's W/H (default 3:2). Mobile can use a gen-fill-expanded
// TALLER variant (see scene.mAr) so the portrait crop magnifies far less.
export function wallGridWidthPctForAspect(cols, env, containerAspect, imgAspect) {
  const realFrac = wallGridWidthCm(cols) / sceneScaleCm(env); // fraction of the image width
  const ia = imgAspect || SCENE_IMG_ASPECT;
  const coverScale = Math.max(1, ia / (containerAspect || ia));
  return Math.min(95, realFrac * coverScale * 100);
}

// name = canonical app frame key (EditImage/pricing/frameselected); wall = webp basename.
// shadow:false -> frames whose webp already reads flat/matte (no drop-shadow).
export const FRAMES = [
  { name: 'classic', wall: 'classic', img: 'd1.png', text: 'קלאסי', badge: { cls: 'pop', txt: 'פופולארי' }, sw: 66, shadow: false },
  { name: 'bold', wall: 'bold', img: 'd2.png', text: 'נועז', sw: 92 },
  { name: 'ever', wall: 'even', img: 'd3.png', text: 'נקי', sw: 92 },
  { name: 'clean', wall: 'clean', img: 'd4.png', text: 'אותנטי', sw: 66, shadow: false },
  { name: 'village', wall: 'village', img: 'd5.png', text: 'כפרי', badge: { cls: 'new', txt: 'חדש' }, sw: 66, shadow: false },
  { name: 'nordic', wall: 'nordic', img: 'd6.png', text: 'נורדי', badge: { cls: 'new', txt: 'חדש' }, sw: 92 },
];

export const DEFAULT_FRAME = 'classic';
export const ENV_SCENE = 'env-living';

// Room scenes: file + real scale (cm the image width spans — see WALL_SCENE_PROPORTIONS.md,
// measured from a known-size anchor) + catalog-thumb placement (grid.top/height are % of
// the thumb; grid.cx nudges the grid horizontally, + = physical right, to dodge decor).
// `light` matches the room's real lighting so the frames sit in the scene (not pasted):
//   angle = gradient direction in deg (90 = light from the LEFT, 270 = from the RIGHT,
//   180 = from above); warm = warm-highlight strength; dim = shadow-side darkening.
// `zoom` overrides the mobile scene zoom (default 1.3) — lower shows more room for a big
// scene. See WALL_SCENE_PROPORTIONS.md.
// `ambient` = how bright the FRAMES should render to match the room's exposure (a print
// in a dim room looks dim). 1 = full brightness; lower = darker room. This is what makes
// the frames sit in the scene instead of glowing over a dark photo.
export const DEFAULT_LIGHT = { angle: 150, warm: 0.1, dim: 0.08, ambient: 0.95 };
export const SCENES = [
  { id: 'living', name: 'סלון', file: 'env-living.webp', scaleCm: 320, grid: { top: 13, height: 42, cx: -4 }, light: { angle: 150, warm: 0.1, dim: 0.07, ambient: 0.97 } },
  { id: 'bedroom', name: 'חדר שינה', file: 'env-bedroom.webp', scaleCm: 320, grid: { top: 12, height: 40, cx: -3 }, light: { angle: 200, warm: 0.1, dim: 0.08, ambient: 0.9 } },
  { id: 'hallway', name: 'מסדרון', file: 'env-hallway.webp', scaleCm: 300, grid: { top: 14, height: 46, cx: 12 }, light: { angle: 120, warm: 0.1, dim: 0.08, ambient: 0.93 } },
  { id: 'kids', name: 'חדר ילדים', file: 'env-kids.webp', scaleCm: 320, grid: { top: 10, height: 42, cx: 0 }, light: { angle: 160, warm: 0.09, dim: 0.07, ambient: 0.96 } },
  // Jerusalem stone (#4): wide room, low console anchor (~165cm) -> ~460cm scene. Warm
  // daylight from the LEFT (balcony/window); big scene -> zoom out more on mobile.
  { id: 'stone', name: 'אבן ירושלמית', file: 'env-il-stone-living.webp', scaleCm: 460, grid: { top: 6, height: 54, cx: 0 }, zoom: 1.0, light: { angle: 95, warm: 0.16, dim: 0.13, ambient: 0.87 } },
  // Study (#15): desk anchor (~130cm) -> ~290cm scene; DIM warm room, warm light from the
  // RIGHT (window) — frames need a strong ambient dim so they don't glow.
  { id: 'study', name: 'חדר עבודה', file: 'env-il-study.webp', scaleCm: 290, grid: { top: 12, height: 44, cx: 6 }, light: { angle: 265, warm: 0.16, dim: 0.12, ambient: 0.56 } },
  // Calm bedroom (#28): queen-bed headboard anchor (~165cm) -> ~385cm; warm light from the
  // RIGHT (window). cx pulled in (-8, not -14) so the mobile centre-crop doesn't clip the
  // grid; zoom 1.2 gives a 3x3 some breathing room.
  { id: 'bedroom-calm', name: 'חדר שינה רגוע', file: 'env-il-bedroom-calm.webp', scaleCm: 385, zoom: 1.2, grid: { top: 7, height: 40, cx: -15, cxM: -2, cy: -14, cyM: -30, posM: 30, offYM: 8 }, light: { angle: 262, warm: 0.12, dim: 0.09, ambient: 0.93 } },
  // Kids · blue (#30): wide bright room (rocking-horse/shelf anchors ~380-400cm); cool
  // white walls, soft flat daylight; grid on the open centre wall. WIDE 5-across -> zoom
  // out on mobile (1.0) so it fits with margin.
  { id: 'kids-blue', name: 'חדר ילדים תכלת', file: 'env-il-kids-blue.webp', scaleCm: 400, zoom: 1.0, grid: { top: 12, height: 34, cx: 6 }, light: { angle: 150, warm: 0.06, dim: 0.06, ambient: 0.98 } },
  // Styled modern (#26): 3-seat sofa anchor (~218cm) -> ~390cm; warm light from the RIGHT
  // (window); grid centred over the sofa. 4-across -> zoom 1.05 on mobile so it fits.
  // cxM pulled left (-5) so the mobile centre-crop lands the grid over the sofa, not right of it.
  { id: 'modern-styled', name: 'מודרני מעוצב', file: 'env-il-modern-styled.webp', scaleCm: 390, zoom: 1.05, focusZoom: 1.4, grid: { top: 8, height: 42, cx: -4, cxM: -5, cyM: -24 }, light: { angle: 262, warm: 0.11, dim: 0.09, ambient: 0.95 } },
  // --- Batch: last-set scenes. Starting geometry (measured roughly from the photo) — tune
  //     per WALL_SCENE_PROPORTIONS.md against the live render. ---
  // Coastal (#17) — long white sofa, sea window right; bright airy white walls. Wide nordic band (5x2).
  { id: 'coastal', name: 'נוף הים', file: 'env-il-coastal.webp', scaleCm: 360, zoom: 1.0, mAr: 0.8, grid: { top: 16, height: 36, cx: -8, cxM: -6, cy: 0, cyM: 4, posM: 44 }, light: { angle: 255, warm: 0.05, dim: 0.05, ambient: 1.0 } },
  // Entry · balatot (#7) — low bench + plant, patterned floor; wide warm wall. Big 6x4 nordic B&W.
  { id: 'entry-balatot', name: 'מבואה · בלטות', file: 'env-il-entry-balatot.webp', scaleCm: 375, zoom: 1.0, focusZoom: 1.5, mAr: 0.8, grid: { top: 8, height: 50, cx: -8, cxM: -8, cy: -6, cyM: -6, posM: 46 }, light: { angle: 150, warm: 0.1, dim: 0.08, ambient: 0.93 } },
  // Music studio (14 records): cozy warm nook, keyboard/desk anchor -> ~300cm. Moody warm
  // lamp from the LEFT, dim room -> low ambient. STARTING values, tune vs live render.
  { id: 'music-studio', name: 'אולפן מוזיקה', file: 'env-il-music-studio.webp', scaleCm: 300, zoom: 1.2, grid: { top: 8, height: 46, cx: 0, cxM: 0 }, light: { angle: 110, warm: 0.15, dim: 0.12, ambient: 0.72 } },
  // Enterprise lobby (36 team portraits): tall corporate reception, 2-seat sofa anchor
  // (~160cm) -> ~560cm wide. Bright daylight from the LEFT (tall windows); big wall -> zoom
  // out on mobile. STARTING values, tune vs live render.
  { id: 'enterprise-lobby', name: 'לובי משרדים', file: 'env-il-enterprise-lobby.webp', scaleCm: 560, zoom: 1.0, grid: { top: 5, height: 60, cx: -4, cxM: -4 }, light: { angle: 105, warm: 0.05, dim: 0.05, ambient: 1.0 } },
];
const sceneRefKey = (ref) => String(ref || '').replace(/\.webp$/, '');
export const sceneByRef = (ref) => SCENES.find((s) => s.id === ref || sceneRefKey(s.file) === sceneRefKey(ref)) || SCENES[0];
export const sceneById = sceneByRef;
export const sceneLight = (env) => sceneByRef(env).light || DEFAULT_LIGHT;
export const sceneAmbient = (env) => { const l = sceneByRef(env).light; return l && l.ambient != null ? l.ambient : 0.95; };
export const sceneMobileZoom = (env) => sceneByRef(env).zoom || 1.3;
// Per-scene multiplier for the click-to-focus zoom (some galleries, e.g. the modern
// 4-across, read too far out on frame click). 1 = the default adaptive zoom.
export const sceneFocusZoom = (env) => sceneByRef(env).focusZoom || 1;
export const LOWRES = 800;

export const frameByName = (name) => FRAMES.find((f) => f.name === name) || FRAMES[0];
export const frameWebp = (name) => `/assets/shopping/frame-${frameByName(name).wall}.webp`;
export const isLow = (w, h) => !!(w && h && w < LOWRES && h < LOWRES);

export function deliveryRange(now = new Date()) {
  const W = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  const M = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  const wknd = { 5: 1, 6: 1 };
  const c = new Date(now);
  c.setHours(0, 0, 0, 0);
  let n = 0, st = null, en = null;
  for (let i = 0; i < 365; i++) {
    c.setDate(c.getDate() + 1);
    if (wknd[c.getDay()]) continue;
    n++;
    if (n === 8) st = new Date(c);
    if (n === 9) { en = new Date(c); break; }
  }
  if (!st) st = en ? new Date(en) : new Date();
  if (!en) en = new Date(st);
  const short = st.getMonth() === en.getMonth()
    ? `${st.getDate()} - ${en.getDate()} ב${M[en.getMonth()]}`
    : `${st.getDate()} ב${M[st.getMonth()]} - ${en.getDate()} ב${M[en.getMonth()]}`;
  const long = `יום ${W[st.getDay()]}, ${st.getDate()} ב${M[st.getMonth()]} - יום ${W[en.getDay()]}, ${en.getDate()} ב${M[en.getMonth()]}`;
  return { short, long };
}

export function randId(len) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i++) s += chars.charAt(Math.floor(Math.random() * 36));
  return s;
}
